import {
  faCowbell,
  faSquareZ,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BoxProps } from '@mui/material';
import { Box, IconButton, styled } from '@mui/material';
import type { DefaultTheme } from '@mui/styles/defaultTheme';
import React from 'react';
import type { Permissions } from '../../../../../../../components/auth/permissions';
import { withAllow } from '../../../../../../../components/hooks/withAllow';
import { Modal } from '../../../../../../../components/modals/v2/helpers/v2.modal.helpers';
import SimpleTooltip from '../../../../../../../components/SimpleTooltip';
import { ProductTypes } from '../../../../../../../types';
import { withShowable } from '../../../../../../_global/lib/withShowable';
import type { Factor } from '../../../api/types/rater-x-preset';
import { useLocale } from '../../../providers/LocaleProvider';
import { UW_ERROR_CLASS } from '../constants';
import { AppliedRulesAndFactorsModalConfig } from '../modals/AppliedRulesAndFactorsModal';
import { useUnderwriterContextOptional } from '../provider/UnderwriterProvider';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

const REQUIRED_PERMISSIONS: Permissions[] = ['accounts:uw'];

function UnderwriterSupplementalInfo(props: AppliedRulesAndFactorsIconProps) {
  const locale = useLocale();
  function openModal(e: React.MouseEvent) {
    e.stopPropagation();
    Modal.show(AppliedRulesAndFactorsModalConfig, {
      data: {
        ...props,
        language: locale.language,
      },
    });
  }

  return (
    <>
      <ShowableAllowed
        allow={REQUIRED_PERMISSIONS}
        show={Boolean(
          props.appliedRules?.length ||
            Object.values(props.factors || {}).length
        )}
        {...props}
        sx={{ display: 'inline', ...props.sx }}
      >
        <SimpleTooltip
          title="Applied guidelines and factors"
          enterDelay={600}
          style={{ display: 'inline', pointerEvents: 'auto' }}
        >
          <IconButton
            size="small"
            onClick={openModal}
            style={{ display: 'inline' }}
          >
            <Icon />
          </IconButton>
        </SimpleTooltip>
      </ShowableAllowed>

      {[
        { title: Warnings.WARNING, inputs: props.warnings },
        { title: Warnings.ERROR, inputs: props.errors },
        { title: Warnings.REFERRAL, inputs: props.referralReasons },
      ]
        .filter(({ inputs }) => inputs?.length && inputs?.length > 0)
        .map(({ title, inputs }, idx) => {
          return (
            <StyledSimpleTooltip
              key={title}
              variant={title}
              global={props.global}
              className={title === Warnings.ERROR ? UW_ERROR_CLASS : ''}
              style={{
                pointerEvents: 'auto',
                marginLeft: idx === 0 ? undefined : '0.5rem',
              }}
              title={<WarningsTooltip inputs={inputs!} title={title} />}
              placement="bottom"
            >
              <FontAwesomeIcon
                icon={faWarning}
                fontSize="1.25rem"
                style={{
                  marginRight: '0.15rem',
                }}
              />
            </StyledSimpleTooltip>
          );
        })}
    </>
  );
}

export default UnderwriterSupplementalInfo;

const ShowableAllowed = withAllow(withShowable(Box));

type AppliedRulesAndFactorsIconProps = {
  appliedRules?: string[];
  factors?: Factor[];
  lostCost?: number;
  warnings?: string[];
  errors?: string[];
  global?: boolean;
  referralReasons?: string[];
} & BoxProps;

export function WarningsTooltip(props: WarningsTooltipProps) {
  const uniqueInputs = Array.from(new Set(props.inputs));
  return (
    <Box p={0.5}>
      {props.title}:
      {uniqueInputs && (
        <Box component="ul" px={1.5} margin="0 auto">
          {uniqueInputs.map((input) => (
            <Box key={input} component="li" py={0}>
              {input}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

type WarningsTooltipProps = {
  inputs: string[];
  title: string;
};

export const StyledSimpleTooltip = styled(SimpleTooltip)(
  ({
    global = false,
    variant,
    theme,
  }: {
    variant: Warnings;
    global?: boolean | undefined;
    theme?: DefaultTheme;
  }) => {
    function getColor() {
      if (variant === Warnings.ERROR) {
        return theme!.palette.common.error;
      }
      if (variant === Warnings.REFERRAL) {
        return theme!.palette.common.cowbellBlue;
      }

      return theme!.palette.common.yellow[500];
    }
    return {
      display: 'inline-block',
      color: getColor(),
      ...(!global && { transform: 'translateY(0.2rem)' }),
    };
  }
);

export enum Warnings {
  WARNING = 'Warnings',
  ERROR = 'Errors',
  REFERRAL = 'Referral Reasons',
  EDIT_REFERRAL = 'Edit Referral Reasons',
}

function Icon() {
  const ctx = useUnderwriterContextOptional();
  const _icon =
    ctx?.state.productType === ProductTypes.zsp ? faSquareZ : faCowbell;

  return <FontAwesomeIcon icon={_icon as IconProp} fontSize="1.25rem" />;
}
